import type { ApolloCache } from "@apollo/client"
import query from "@/v2-backoffice-client/translation/catalog/translation.catalog.query.gql"
import type { TranslationCatalogQuery } from "@/v2-backoffice-client/translation/catalog/__types__/translation.catalog.query"

type CachedText =
  TranslationCatalogQuery["getFeatureTranslations"][number]["Texts"][number]

export type CatalogCachePatch = {
  // TranslationRow.code: feature Code or type-value ObjectKey.
  code: string
  objectType: "Feature" | "TypeValue"
  texts: {
    LanguageCode: string
    Name: string | null
    Description?: string | null
  }[]
}

function mergeTexts(
  existing: readonly CachedText[],
  updates: CatalogCachePatch["texts"],
): CachedText[] {
  const merged = [...existing]
  for (const update of updates) {
    const next: CachedText = {
      __typename: "TranslationText",
      LanguageCode: update.LanguageCode,
      Name: update.Name,
      Description: update.Description ?? null,
    }
    const index = merged.findIndex(
      (text) => text.LanguageCode === update.LanguageCode,
    )
    if (index >= 0) merged[index] = next
    else merged.push(next)
  }
  return merged
}

/**
 * Writes just-saved translation texts straight into the cached catalog query
 * instead of refetching it. The catalog is ~8k rows; a network refetch after
 * every save/AI-fill left the table stale for seconds. The mutations echo the
 * exact values that were written, so patching the cache locally is both
 * instant and correct — the board memo rebuilds from the updated cache and
 * every derived surface (missing counts, category groups) follows.
 */
export function patchTranslationCatalogCache(
  cache: ApolloCache<unknown>,
  patches: CatalogCachePatch[],
) {
  if (patches.length === 0) return
  const featurePatches = new Map<string, CatalogCachePatch>()
  const typeValuePatches = new Map<string, CatalogCachePatch>()
  for (const patch of patches) {
    const target =
      patch.objectType === "TypeValue" ? typeValuePatches : featurePatches
    target.set(patch.code, patch)
  }

  cache.updateQuery<TranslationCatalogQuery>({ query }, (data) => {
    if (!data) return data
    return {
      ...data,
      getFeatureTranslations: data.getFeatureTranslations.map((feature) => {
        const patch = featurePatches.get(feature.Code)
        if (!patch) return feature
        return { ...feature, Texts: mergeTexts(feature.Texts, patch.texts) }
      }),
      getTypeValueTranslations: data.getTypeValueTranslations.map(
        (typeValue) => {
          const patch = typeValuePatches.get(typeValue.ObjectKey)
          if (!patch) return typeValue
          return {
            ...typeValue,
            Texts: mergeTexts(typeValue.Texts, patch.texts),
          }
        },
      ),
    }
  })
}
